import { type FormEvent, type ReactNode, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import {
  Banner,
  Button,
  Chip,
  ChipGroup,
  Flex,
} from '@spotify-internal/encore-web';
import {
  type FormFieldsHandle,
  FormFieldsProvider,
  FormTextareaField,
} from '@spotify/confidence-core-react';

import {
  ONBOARDING_STARTING_POINTS,
  type SurveyAnswers,
} from '../../config/onboardingSurvey';
import { OnboardingStepLayout } from '../OnboardingStepLayout';
import { CustomRoleFormField } from './CustomRoleFormField';
import { RoleFormField } from './RoleFormField';
import { SurveyActions } from './SurveyActions';
import {
  type SurveyFormValues,
  isRoleAnswered,
  toSurveyAnswers,
} from './surveyForm';

const STEPS = ['motivation', 'role'] as const;
type IntroductionStep = (typeof STEPS)[number];
const STEP_LABELS: Record<IntroductionStep, string> = {
  motivation: 'Why you’re here',
  role: 'Your role',
};

type AgentWelcomeProps = {
  orgName?: string;
  renderProgress?: (questionProgress: string) => ReactNode;
  onBack?: () => void;
  isSaving: boolean;
  hasSaveError: boolean;
  onContinue: (options: { answers: SurveyAnswers }) => void;
};

export const AgentWelcome = ({
  orgName,
  renderProgress,
  onBack,
  isSaving,
  hasSaveError,
  onContinue,
}: AgentWelcomeProps): JSX.Element => {
  const [motivation, setMotivation] = useState('');
  const [step, setStep] = useState<IntroductionStep>('motivation');
  const stepRef = useRef<HTMLDivElement>(null);
  const formFieldsRef = useRef<FormFieldsHandle<SurveyFormValues>>(null);

  const changeStep = (nextStep: IntroductionStep): void => {
    flushSync(() => setStep(nextStep));
    stepRef.current?.focus();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (isSaving) return;
    if (step === 'motivation') {
      if (motivation.trim()) changeStep('role');
    } else {
      const values = formFieldsRef.current?.values ?? {};
      if (isRoleAnswered(values))
        onContinue({ answers: toSurveyAnswers(values) });
    }
  };

  const skipQuestion = (): void => {
    if (isSaving) return;
    if (step === 'motivation') {
      setMotivation('');
      changeStep('role');
    } else {
      onContinue({ answers: {} });
    }
  };

  const goBack = (): void => {
    if (step === 'motivation') {
      onBack?.();
    } else {
      changeStep('motivation');
    }
  };

  const canSubmitByStep: Record<IntroductionStep, boolean | undefined> = {
    motivation: Boolean(motivation.trim()),
    role: undefined,
  };

  const backButton = (step !== 'motivation' || onBack) && (
    <Button
      type="button"
      variant="tertiary"
      size="medium"
      disabled={isSaving}
      onClick={goBack}
     data-track-source-anchor="34d1ff2062">
      Back
    </Button>
  );

  return (
    <OnboardingStepLayout
      title="Meet your Confidence agent"
      headerSize={step === 'motivation' ? 'large' : 'compact'}
      description={
        step === 'motivation'
          ? `${orgName ? `Welcome to ${orgName}. ` : ''}A few questions will help you get started with the agent. It can help you plan a test, release a feature, or investigate a product problem.`
          : 'Almost done. Your role helps the agent tailor its guidance.'
      }
      progress={renderProgress?.(
        `${STEPS.indexOf(step) + 1} of ${STEPS.length}`,
      )}
      trackId="region.agent-welcome"
    >
      <form onSubmit={handleSubmit} data-track-source-anchor="5c888ce7dc;agent-welcome-form">
        <FormFieldsProvider<SurveyFormValues> ref={formFieldsRef}>
          <Flex direction="column" gap="looser">
            {hasSaveError && (
              <Banner colorSet="negative" contextual role="alert">
                We couldn’t finish setting up. Your answers are still here. Try
                again.
              </Banner>
            )}
            <div
              ref={stepRef}
              tabIndex={-1}
              role="group"
              aria-label={STEP_LABELS[step]}
            >
              {step === 'motivation' && (
                <Flex direction="column" gap="base">
                  <FormTextareaField
                    label="What brought you to Confidence today?"
                    name="motivation"
                    value={motivation}
                    onChange={event => setMotivation(event.currentTarget.value)}
                    helperText="Tell us in your own words, or choose a starting point."
                    rows={3}
                    maxLength={1000}
                    disabled={isSaving}
                   data-track-source-anchor="3ad7f02a52"/>
                  <ChipGroup
                    mode="action"
                    layout="multi-line"
                    aria-label="Starting points"
                  >
                    {ONBOARDING_STARTING_POINTS.map(option => (
                      <Chip
                        type="button"
                        key={option.id}
                        disabled={isSaving}
                        onClick={() => setMotivation(option.label)}
                       data-track-source-anchor="b160ae497a;agent-welcome-chip">
                        {option.label}
                      </Chip>
                    ))}
                  </ChipGroup>
                </Flex>
              )}
              {step === 'role' && (
                <Flex direction="column" gap="tighter">
                  <RoleFormField />
                  <CustomRoleFormField />
                </Flex>
              )}
            </div>
            <SurveyActions
              isSaving={isSaving}
              onSkip={skipQuestion}
              backAction={backButton}
              submitLabel="Next"
              skipLabel="Skip for now"
              loadingAriaLabel="Getting ready"
              canSubmit={canSubmitByStep[step]}
            />
          </Flex>
        </FormFieldsProvider>
      </form>
    </OnboardingStepLayout>
  );
};
